import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { createColumnHelper } from "@tanstack/react-table";
import { api } from "@/lib/api";
import { usePaged, formatMinor } from "@/lib/hooks";
import { useEntityAction } from "@/lib/mutations";
import { useAuth } from "@/auth/auth";
import { PagedTable } from "@/components/PagedTable";
import { RowActions } from "@/components/RowActions";
import { Card, ErrorNote, Spinner, StatusBadge } from "@/components/ui";
import type { CommissionRow, DriverDebtSummary, FinanceSummaryRow, MoneyRow } from "@/lib/types";
import { PayoutState } from "@/components/PayoutState";
import { companyToday, formatCompanyDate } from "@/lib/time";

const col = createColumnHelper<MoneyRow>();

type Period = "month" | "last" | "all";

/** The service-date window (Dubai dates) of a period choice. */
function periodWindow(p: Period): { service_from?: string; service_to?: string } {
  if (p === "all") return {};
  const [y, m] = companyToday().split("-").map(Number);
  const first = p === "month" ? new Date(Date.UTC(y, m - 1, 1)) : new Date(Date.UTC(y, m - 2, 1));
  const last = new Date(Date.UTC(first.getUTCFullYear(), first.getUTCMonth() + 1, 0));
  const iso = (d: Date) => d.toISOString().slice(0, 10);
  return { service_from: iso(first), service_to: iso(last) };
}

/** Owner rule: EARNED = completed services only; what is still to happen is
 * EXPECTED — two separate blocks, never one "revenue" figure. */
function SummaryCards() {
  const { t } = useTranslation();
  const [period, setPeriod] = useState<Period>("month");
  const win = periodWindow(period);
  const query = useQuery({
    queryKey: ["finance", "summary", period],
    queryFn: () => {
      const qs = new URLSearchParams(win as Record<string, string>).toString();
      return api.get<FinanceSummaryRow[]>(`/finance/summary${qs ? `?${qs}` : ""}`);
    },
  });

  const block = (row: FinanceSummaryRow, kind: "earned" | "expected") => {
    const v = (f: string) =>
      formatMinor(row[`${kind}_${f}_minor` as keyof FinanceSummaryRow] as number, row.currency);
    return (
      <div data-testid={`finance-${kind}`}>
        <div className="text-xs font-semibold text-sand-muted">
          {t(`finance.${kind}`)}
          <span className="ml-2 font-normal">{t(`finance.${kind}Hint`)}</span>
        </div>
        <dl className="mt-1 space-y-1 text-sm">
          <div className="flex items-baseline justify-between">
            <dt className="text-sand-muted">{t("finance.revenue")}</dt>
            <dd className="nm-amount">{v("revenue")}</dd>
          </div>
          <div className="flex items-baseline justify-between">
            <dt className="text-sand-muted">{t("finance.cost")}</dt>
            <dd className="nm-amount">{v("cost")}</dd>
          </div>
          <div className="flex items-baseline justify-between">
            <dt className="text-sand-muted">{t("finance.commission")}</dt>
            <dd className="nm-amount">{v("commission")}</dd>
          </div>
          <div className="flex items-baseline justify-between border-t border-olive-600 pt-1">
            <dt>{t("finance.grossProfit")}</dt>
            <dd className="nm-amount">{v("profit")}</dd>
          </div>
        </dl>
      </div>
    );
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        <label className="flex items-center gap-2 text-sm text-sand-muted">
          {t("finance.period")}
          <select
            className="rounded-md border border-olive-600 bg-olive-900 px-2 py-1 text-sm text-sand"
            value={period}
            onChange={(e) => setPeriod(e.target.value as Period)}
          >
            <option value="month">{t("finance.periodMonth")}</option>
            <option value="last">{t("finance.periodLastMonth")}</option>
            <option value="all">{t("finance.periodAll")}</option>
          </select>
        </label>
        <span className="text-xs text-sand-muted">{t("finance.periodHint")}</span>
      </div>
      {query.isLoading ? (
        <Spinner />
      ) : query.isError ? (
        <ErrorNote message={(query.error as Error).message} />
      ) : !query.data?.length ? (
        <p className="text-sm text-sand-muted">{t("finance.empty")}</p>
      ) : (
        <div className="grid gap-4 lg:grid-cols-2">
          {query.data.map((row) => (
            <Card key={row.currency}>
              <div className="mb-2 text-sm font-semibold text-gold">{row.currency}</div>
              <div className="grid gap-4 sm:grid-cols-2">
                {block(row, "earned")}
                {block(row, "expected")}
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

/** Phase 13.1 part 5: per-driver "collected but not yet transferred" totals. */
function DriverDebtsPanel() {
  const { t } = useTranslation();
  const query = useQuery({
    queryKey: ["finance", "driver-debts"],
    queryFn: () => api.get<DriverDebtSummary[]>("/finance/driver-debts"),
  });

  return (
    <div className="space-y-2">
      <h2 className="text-sm font-semibold text-sand">{t("finance.driverDebts")}</h2>
      <p className="text-xs text-sand-muted">{t("finance.driverDebtsHint")}</p>
      {query.isLoading ? (
        <Spinner />
      ) : query.isError ? (
        <ErrorNote message={(query.error as Error).message} />
      ) : !query.data?.length ? (
        <p className="text-sm text-sand-muted">{t("finance.noDriverDebts")}</p>
      ) : (
        <Card>
          <ul className="divide-y divide-olive-700 text-sm">
            {query.data.map((row) => (
              <li key={row.driver_id} className="flex items-center gap-3 py-1.5">
                <span className="font-medium">{row.driver_name}</span>
                <span className="text-sand-muted">
                  {t("finance.driverDebtLines", { count: row.lines_count })}
                </span>
                <span className="ml-auto font-semibold">
                  {formatMinor(row.owed_aed_minor, "AED")}
                </span>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
}

/** One cross-booking money-line table (payments / costs / commissions). */
function MoneyLineTable({
  path,
  titleKey,
  emptyKey,
}: {
  path: "payments" | "costs" | "commissions";
  titleKey: string;
  emptyKey: string;
}) {
  const { t } = useTranslation();
  const { can } = useAuth();
  const canWrite = can("finance:write");
  const [showDeleted, setShowDeleted] = useState(false);
  const rows = usePaged<MoneyRow>(["finance", path], `/finance/${path}`, {
    include_deleted: showDeleted ? "true" : undefined,
  });
  const action = useEntityAction([
    ["finance", path],
    ["finance", "summary"],
  ]);

  const columns = useMemo(
    () => [
      col.accessor("created_at", {
        header: () => t("fields.date"),
        cell: (c) => formatCompanyDate(c.getValue()),
      }),
      col.accessor("amount", {
        header: () => t("fields.amount"),
        cell: (c) => {
          // A voided row, or a commission that counts nowhere (its booking is
          // cancelled / a draft), is shown struck through — never as money.
          const dead =
            !!c.row.original.deleted_at ||
            (c.row.original as CommissionRow).payout_state === "void";
          return (
            <span className={dead ? "line-through text-sand-muted" : ""}>
              {formatMinor(c.row.original.amount_minor, c.row.original.currency)}
            </span>
          );
        },
      }),
      ...(path === "commissions"
        ? [
            col.display({
              id: "agent",
              header: () => t("agentsPage.agent"),
              cell: (c) => (c.row.original as CommissionRow).agent_name ?? "—",
            }),
          ]
        : []),
      col.accessor("status", {
        header: () => t(path === "commissions" ? "agentsPage.payoutStatus" : "fields.status"),
        cell: (c) => {
          // A commission's status is its payout state — «Ожидается» until its lines
          // are completed, «К выплате», «Выплачено» — not the stored workflow flag.
          const state = (c.row.original as CommissionRow).payout_state;
          if (path === "commissions" && state) return <PayoutState state={state} />;
          // A supplier cost has no status of its own — a quiet dash, not an empty pill.
          return c.getValue() ? <StatusBadge status={c.getValue()} /> : "—";
        },
      }),
      col.accessor("booking_id", {
        header: () => t("fields.booking"),
        cell: (c) => (
          <Link
            className="text-sand hover:text-gold"
            to={`/bookings/${c.row.original.booking_number ?? c.getValue()}`}
          >
            {c.row.original.booking_number ?? t("common.open")}
          </Link>
        ),
      }),
      ...(canWrite
        ? [
            col.display({
              id: "actions",
              header: () => t("common.actions"),
              cell: (c) => {
                const id = c.row.original.id;
                return (
                  <RowActions
                    deleted={!!c.row.original.deleted_at}
                    canWrite={canWrite}
                    busy={action.isPending}
                    deleteLabel={t("finance.void")}
                    onDelete={() =>
                      action.mutate(() => api.del(`/finance/${path}/${id}`))
                    }
                    onRestore={() =>
                      action.mutate(() =>
                        api.post(`/finance/${path}/${id}/restore`),
                      )
                    }
                  />
                );
              },
            }),
          ]
        : []),
    ],
    [t, canWrite, action, path],
  );

  return (
    <div className="space-y-2">
      <h2 className="text-sm font-semibold text-sand">{t(titleKey)}</h2>
      <PagedTable
        columns={columns}
        data={rows.items}
        query={rows.query}
        total={rows.total}
        page={rows.page}
        pageCount={rows.pageCount}
        onPage={rows.setPage}
        empty={t(emptyKey)}
        toolbar={
          canWrite ? (
            <label className="flex items-center gap-1.5 text-sm text-sand-muted">
              <input
                type="checkbox"
                checked={showDeleted}
                onChange={(e) => {
                  setShowDeleted(e.target.checked);
                  rows.resetPage();
                }}
              />
              {t("finance.showVoided")}
            </label>
          ) : undefined
        }
      />
    </div>
  );
}

export default function FinancePage() {
  const { t } = useTranslation();

  return (
    <div className="space-y-6">
      <div className="space-y-3">
        <h1 className="text-lg font-semibold">{t("finance.title")}</h1>
        <SummaryCards />
      </div>
      <DriverDebtsPanel />
      <p className="text-xs text-sand-muted">{t("finance.tablesHint")}</p>
      <MoneyLineTable path="payments" titleKey="finance.payments" emptyKey="finance.noPayments" />
      <MoneyLineTable path="costs" titleKey="finance.costs" emptyKey="finance.noCosts" />
      <MoneyLineTable
        path="commissions"
        titleKey="finance.commissions"
        emptyKey="finance.noCommissions"
      />
    </div>
  );
}
